import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import "../styles/google.scss";

/** 3D Fluent Emoji (MIT, public/feature-icons) orbiting the rocket. */
const ORBIT = ["themes", "channels", "fraud", "gtm", "promotions", "import", "mcp", "n8n"];

/** Capabilities shown one by one while the store is built. */
const FEATURES = [
  { icon: "channels", key: "ai" },
  { icon: "themes", key: "themes" },
  { icon: "fraud", key: "fraud" },
  { icon: "promotions", key: "promotions" },
  { icon: "gtm", key: "gtm" },
  { icon: "import", key: "import" },
  { icon: "mcp", key: "mcp" },
  { icon: "n8n", key: "n8n" },
];

const STEPS = ["database", "catalog", "admin", "open"];

/** Map of the API's progress (status.step) onto the four visible steps. */
const stepIndex = (state, step) => {
  if (state === "ready") return 4;
  if (step === "warming" || step === "store") return 3;
  if (step === "account") return 2;
  if (step === "active") return 1;
  return 0;
};

/**
 * Full-screen "your store is being built" page for the Google signup. It never
 * pretends to be finished: progress creeps toward 92% with time, and only the
 * API's "ready" (job done, store answering) completes it.
 */
export const StoreBuildingLoader = ({ storeName, state, step, failed, onRetry }) => {
  const { t } = useTranslation();
  const [feature, setFeature] = useState(0);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const rotate = setInterval(() => setFeature((i) => (i + 1) % FEATURES.length), 3600);
    const clock = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => {
      clearInterval(rotate);
      clearInterval(clock);
    };
  }, []);

  const done = state === "ready";
  const current = Math.max(stepIndex(state, step), Math.min(2, Math.floor(elapsed / 12)));
  const progress = done ? 100 : Math.min(92, 8 + current * 18 + elapsed * 0.9);
  const f = FEATURES[feature];

  return (
    <div className={`sp-build${done ? " is-done" : ""}`} role="status" aria-live="polite">
      <div className="sp-build-glow sp-build-glow--a" />
      <div className="sp-build-glow sp-build-glow--b" />

      <div className="sp-build-inner">
        <img className="sp-build-logo" src="/logo-text-white.png" alt="StorkPlus" />

        <div className="sp-orbit" aria-hidden="true">
          <div className="sp-orbit-ring" />
          <div className="sp-orbit-track">
            {ORBIT.map((icon, i) => (
              <span
                key={icon}
                className="sp-orbit-item"
                style={{ "--i": i, "--n": ORBIT.length }}
              >
                <img src={`/feature-icons/${icon}.webp`} alt="" />
              </span>
            ))}
          </div>
          <img
            className="sp-orbit-core"
            src={done ? "/feature-icons/party.webp" : "/feature-icons/rocket.webp"}
            alt=""
          />
        </div>

        {failed ? (
          <>
            <h1 className="sp-build-title">{t("google.failedTitle", "Something went wrong")}</h1>
            <p className="sp-build-sub">{failed}</p>
            {onRetry ? (
              <button type="button" className="sp-build-retry" onClick={onRetry}>
                {t("google.retry", "Try again")}
              </button>
            ) : null}
          </>
        ) : (
          <>
            <h1 className="sp-build-title">
              {done
                ? t("google.readyTitle", "Your store is ready!")
                : t("google.buildingTitle", "Building {{store}}…", { store: storeName || t("google.yourStore", "your store") })}
            </h1>
            <p className="sp-build-sub">
              {done
                ? t("google.readySub", "Opening your dashboard…")
                : t("google.buildingSub", "This takes about a minute. Keep this page open — we'll sign you in automatically.")}
            </p>

            <div className="sp-build-bar" aria-hidden="true">
              <span style={{ width: `${progress}%` }} />
            </div>

            <ol className="sp-build-steps">
              {STEPS.map((key, i) => (
                <li key={key} className={i < current || done ? "is-done" : i === current ? "is-active" : ""}>
                  <span className="sp-build-dot">{i < current || done ? "✓" : i + 1}</span>
                  {t(`google.steps.${key}`)}
                </li>
              ))}
            </ol>

            <div className="sp-build-card" key={f.key}>
              <img src={`/feature-icons/${f.icon}.webp`} alt="" />
              <div>
                <strong>{t(`google.features.${f.key}.title`)}</strong>
                <span>{t(`google.features.${f.key}.text`)}</span>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default StoreBuildingLoader;
